import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import UserSettingsService from "@services/userSettingsService";

import { useGetFilesQuery, useLazyGetFilesQuery } from "./index";
import { defaultPageSize, GetFilesParams } from "./rtkClient";

type GetFilesQueryOptions = Omit<Parameters<typeof useGetFilesQuery>[1], "selectFromResult">;
type PageZeroData = ReturnType<typeof useGetFilesQuery>["data"];

function getDefaultGroupFileParams(groupId: number): GetFilesParams {
    return {
        ...UserSettingsService.getGroupSidePanelFileTabParams(),
        groups: [groupId],
    };
}

// Builds the params actually sent to the files API. Used by every files query
// so the file prefetch and the page request resolve to the same RTK Query
// cache key — change the rules here once and both stay aligned.
function normalizeFilesQueryParams(params: GetFilesParams, workspaceId: number | null): GetFilesParams {
    const normalized: GetFilesParams = { ...params };
    if (workspaceId !== null) {
        normalized.workspaceId = workspaceId;
    }

    const hasGroupFilter = Array.isArray(normalized.groups) && normalized.groups.length > 0;
    if (!hasGroupFilter) {
        delete normalized.groups;
        delete normalized.includeParentFiles;
    }

    return normalized;
}

function useCurrentWorkspaceFiles(params: GetFilesParams, options?: GetFilesQueryOptions) {
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const paramsCopy = normalizeFilesQueryParams(params, workspaceId);

    const optionsCopy = { ...options, skip: false };
    if (options && "skip" in options) {
        optionsCopy.skip = !!options.skip;
    }

    if (!workspaceId) {
        optionsCopy.skip = true;
    }

    return useGetFilesQuery(paramsCopy, optionsCopy);
}

type UseAllCurrentWorkspaceFilesOptions = {
    preferCacheValue?: boolean;
    skip?: boolean;
};

type PageFetch = {
    page0Data: PageZeroData;
    paramsKey: string;
    failure: { error: unknown; requestId?: string } | null;
};

function useAllCurrentWorkspaceFiles(
    params: GetFilesParams,
    { preferCacheValue = true, skip = false }: UseAllCurrentWorkspaceFilesOptions = {},
) {
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const [triggerGetFiles] = useLazyGetFilesQuery();
    const [allFiles, setAllFiles] = useState<API.File[]>([]);
    const [pageFetch, setPageFetch] = useState<PageFetch | null>(null);
    const fetchIdRef = useRef(0);

    const normalizedParams = normalizeFilesQueryParams(params, workspaceId);
    const paramsKey = JSON.stringify(normalizedParams);

    // Subscribing to page 0 keeps an active RTK cache entry so tag invalidation (from
    // mutations) triggers a re-fetch. Its data seeds the accumulator, so the lazy loop
    // only fetches pages 1+, avoiding a redundant page-0 request.
    const {
        data: page0Data,
        isFetching: isPage0Fetching,
        isError: isPage0Error,
        error: page0Error,
        requestId: page0RequestId,
        refetch: refetchPage0,
    } = useGetFilesQuery({ ...normalizedParams, pageSize: defaultPageSize, pageIndex: 0 }, { skip: skip || !workspaceId });

    const needsPageFetch = !skip && Boolean(workspaceId) && Boolean(page0Data);
    const isFetching = needsPageFetch && (!pageFetch || pageFetch.page0Data !== page0Data || pageFetch.paramsKey !== paramsKey);
    const pageFailure = isFetching ? null : (pageFetch?.failure ?? null);

    const fetchAll = useCallback(async () => {
        if (!workspaceId || !page0Data) {
            return;
        }

        const fetchId = ++fetchIdRef.current;
        const accumulated: API.File[] = [...page0Data.list];
        const queryParams: GetFilesParams = { ...JSON.parse(paramsKey), pageSize: defaultPageSize };
        let pageIndex = 1;
        let failure: { error: unknown; requestId?: string } | null = null;

        try {
            while (accumulated.length < page0Data.count && page0Data.list.length > 0) {
                // eslint-disable-next-line no-await-in-loop
                const { data, error, requestId } = await triggerGetFiles({ ...queryParams, pageIndex }, preferCacheValue);

                if (fetchId !== fetchIdRef.current) {
                    return;
                }

                if (error) {
                    failure = { error, requestId };
                    break;
                }

                if (!data) {
                    break;
                }
                accumulated.push(...data.list);
                if (accumulated.length >= data.count || data.list.length === 0) {
                    break;
                }
                pageIndex += 1;
            }

            if (!failure) {
                setAllFiles((prev) =>
                    prev.length === accumulated.length && prev.every((file, index) => file === accumulated[index])
                        ? prev
                        : accumulated,
                );
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                setPageFetch({ page0Data, paramsKey, failure });
            }
        }
    }, [paramsKey, workspaceId, triggerGetFiles, preferCacheValue, page0Data]);

    useEffect(() => {
        if (!skip) {
            fetchAll();
        }
    }, [fetchAll, skip]);

    return {
        data: allFiles,
        isFetching: isPage0Fetching || isFetching,
        isLoading: allFiles.length === 0 && (isPage0Fetching || isFetching),
        isError: isPage0Error || pageFailure !== null,
        error: isPage0Error ? page0Error : pageFailure?.error,
        requestId: isPage0Error ? page0RequestId : pageFailure?.requestId,
        refetch: refetchPage0,
    };
}

export { getDefaultGroupFileParams, normalizeFilesQueryParams, useAllCurrentWorkspaceFiles, useCurrentWorkspaceFiles };
